import { defineComponent } from "vue";
import { Container } from "aurelia-dependency-injection";
import { mapActions, mapState } from "pinia";
import { useApp } from "../../App/useApp";
import moment from "moment";

import config from "../../../common/config";
import { ApplicationEnum, ParentPermissionEnum, PermissionActionEnum } from "../../../enums/enums";
import { IGetAgency, IGetOrganization } from "../../../services/agency.service";
import { TaskService } from "../../../services/task.service";
import { BranchesService } from "../../../services/branches-service";
import { IUnassignedPatientVisitRequest, IVisitCountResponse } from "../../../resources-vue/vue-interfaces/i-task";
import { IBranchResponse } from "../../../resources-vue/vue-interfaces/i-branch";
import { PermissionManager } from "../../../common/utilities/permission-manager";
import { useLoadingIndicator } from "../../Common/LoadingIndicator/store/useLoadingIndicator";
import { AnalyticsCategory, AnalyticsService } from "../../../services/analytics.service";
import { ToastrService } from "../../../services/toastr.service";
import { DownloadService } from "../../../services/download.service";

import UnassignedPatientVisitsHCHOSPAL from "./UnassignedPatientVisitsHCHOSPAL/UnassignedPatientVisitsHCHOSPAL.vue";
import UnassignedPatientVisitsHomeHealth from "./UnassignedPatientVisitsHomeHealth/UnassignedPatientVisitsHomeHealth.vue";
import MciLayout from "../../../resources-vue/vue-elements/MciLayout/Mcilayout.vue";

export default defineComponent({
    components: {
        UnassignedPatientVisitsHCHOSPAL,
        UnassignedPatientVisitsHomeHealth,
        MciLayout
    },
    data() {
        return {
            _analyticsServices: null as AnalyticsService,
            _taskService: null as TaskService,
            _branchService: null,
            _permissionManager: null,
            _toastrService: null as ToastrService,
            _downloadService: null as DownloadService,
            tabs: [],
            activeTab: null,
            allowedTabs: [ApplicationEnum.HomeCare, ApplicationEnum.AxxessHospiceFE, ApplicationEnum.AxxessPalliative],
            visitCounts: [] as IVisitCountResponse[],
            selectOrganization: [] as IGetOrganization[],
            agencyList: [] as IGetAgency[],
            branches: [] as IBranchResponse[],
            haveAllTabsCount: true,
            ApplicationEnum
        };
    },
    async created() {
        this.showLoading(true);
        this._taskService = Container.instance.get(TaskService);
        this._branchService = Container.instance.get(BranchesService);
        this._permissionManager = Container.instance.get(PermissionManager);
        this._analyticsServices = Container.instance.get(AnalyticsService);
        this._toastrService = Container.instance.get(ToastrService);
        this._downloadService = Container.instance.get(DownloadService);
        const organizationId = localStorage.getItem(config.organizationId);
        this.selectOrganization = this.organizations.filter((organization) => organization.id == organizationId);
        this.branches = await this._branchService.getAllBranches();
        this.agencyList = this.selectOrganization[0].agencies;
        this.agencyList = this.agencyList.map((agency) => {
            return {
                ...agency,
                id: agency.id?.split("_")[0]
            };
        });
        this.loadTabs();
        this.showLoading(false);
    },

    computed: {
        ...mapState(useApp, ["organizations"])
    },

    methods: {
        ...mapActions(useLoadingIndicator, { showLoading: "SET_LOADING" }),

        handleTabChange(tab: any) {
            // To prevent navigation for solutions that are unavailable
            if (!this.allowedTabs.includes(tab.id)) {
                return;
            }
            this.activeTab = tab.id;
            this.$router.push({ query: { application: this.activeTab } });
            this._analyticsServices.logEvent({
                category: AnalyticsCategory.CentralMenuPages,
                action: `Unassigned-${tab.name}`
            });
        },
        async loadTabs() {
            this.tabs = [
                {
                    name: "Home Care",
                    id: ApplicationEnum.HomeCare,
                    count: 0
                },
                {
                    name: "Hospice",
                    id: ApplicationEnum.AxxessHospiceFE,
                    count: 0
                },
                {
                    name: "Palliative",
                    id: ApplicationEnum.AxxessPalliative,
                    count: 0
                }
            ];
            this.tabs = this.tabs.filter((tab) => this.agencyList.some((agency) => tab.id == agency.application));

            const allowedTab = this.tabs.find((tab) => this.allowedTabs.includes(tab.id));
            if (Object.keys(this.$route.query).length != 0) {
                this.activeTab = parseInt(this.$route.query.application as string, 10);
            } else {
                this.activeTab = allowedTab ? allowedTab.id : null;
                this.$router.push({ query: { application: this.activeTab } });
            }
        },

        getAllTabsCount() {
            const schedulerViewPermissions = this._permissionManager.getPermmission(
                ParentPermissionEnum.scheduler,
                PermissionActionEnum.canView
            );
            const startOfWeek = moment();
            const endOfWeek = moment().add(7, "days");

            let payload: IUnassignedPatientVisitRequest = {
                startDate: startOfWeek.format("MM/DD/YYYY"),
                endDate: endOfWeek.format("MM/DD/YYYY"),
                teamIds: [],
                tagsIds: [],
                page: 1,
                pageLength: 10,
                sortBy: "userFirstName",
                order: "asc",
                accountIds: [],
                application: this.activeTab.id,
                userId: "",
                locationIds: [],
                defaultLocationIds: []
            };
            this.tabs.forEach((tab) => {
                if (
                    this.allowedTabs.some((allowedtab) => tab.id === allowedtab) &&
                    schedulerViewPermissions.some((permission: any) => permission.application == tab.id)
                ) {
                    payload.accountIds = this.agencyList
                        .filter((agency) => agency.application === tab.id)
                        .map((x: any) => x.id);
                    payload.locationIds = payload.defaultLocationIds = this.branches
                        .filter((branch) => branch.application === tab.id)
                        .map((x: any) => x.id);
                    payload.application = tab.id;
                    this.getUnassignedPatientsVisits(payload, tab.id);
                }
            });
        },
        async getUnassignedPatientsVisits(payload: any, application: any) {
            if (this.haveAllTabsCount) {
                this.haveAllTabsCount = false;
                this.getAllTabsCount();
            }
            const response = await this._taskService.getUnassignedPatientVisits(payload);
            this.tabs.forEach((tab) => {
                if (tab.id == application) {
                    tab.count = response.data.ItemCount || 0;
                }
            });
            return response;
        },

        async exportUnassignedPatientsVisits(payload: IUnassignedPatientVisitRequest) {
            try {
                const response = await this._taskService.exportUnassignedPatientVisits({ ...payload, export: true });
                const warningMessage = response.headers.get("x-warning-message");
                if (warningMessage) {
                    this._toastrService.warning({
                        title: `Export Excel Warning`,
                        message: warningMessage
                    });
                }
                this._downloadService.downloadFile(response, null);
                this._toastrService.success({
                    title: `Success`,
                    message: `Unassigned Patient Visits Export Excel Downloaded.`
                });
            } catch (error) {
                console.error(error);
                this._toastrService.error({
                    title: `Error`,
                    message:
                        "There was a problem while exporting excel of the Unassigned Patient Visits. Please try again."
                });
            }
        }
    }
});
